import { useEffect, useState } from 'react';
import { usePatientView } from '../App';
import type { Route } from '../lib/routes';
import type { AppSession, MeView, PatientView } from '../lib/types';
import type { AuthConfig } from '../lib/home';
import { goTo, markWelcomed, tabHash, type PatientTab } from '../lib/routes';
import { fmtCoins } from '../lib/format';
import { displayName } from '../lib/session';
import { HeartMark, Icon } from '../components/HeartMark';
import { Toast } from '../components/Bits';
import { UserMenu } from '../components/UserMenu';
import { Onboarding } from './patient/Onboarding';
import { HomeTab } from './patient/HomeTab';
import { HealthTab } from './patient/HealthTab';
import { CoachTab } from './patient/CoachTab';
import { RewardsTab } from './patient/RewardsTab';
import { ProfileTab } from './patient/ProfileTab';
import { BpLogFlow } from './patient/BpLogFlow';
import { SymptomFlow } from './patient/SymptomFlow';
import { VitalLogFlow } from './patient/VitalLogFlow';
import { DevicesPage } from './patient/DevicesPage';
import { MessagesPage } from './patient/MessagesPage';

/**
 * The top bar. Devices is a section of its own rather than a page you reach and then cannot see
 * yourself on: it used to render with Home lit up, which tells you you are somewhere you are not.
 */
const NAV: Array<{ page: PatientTab | 'devices'; label: string; icon: 'home' | 'health' | 'coach' | 'rewards' | 'device' }> = [
  { page: 'home', label: 'Home', icon: 'home' },
  { page: 'health', label: 'Health', icon: 'health' },
  { page: 'coach', label: 'Coach', icon: 'coach' },
  { page: 'rewards', label: 'Rewards', icon: 'rewards' },
  { page: 'devices', label: 'Devices', icon: 'device' },
];

const navHref = (page: PatientTab | 'devices'): string => (page === 'devices' ? '#/devices' : tabHash(page));

/**
 * The patient app, as a WEB app: a sticky top bar with navigation, and a centred content shell whose
 * pages lay themselves out across the width they are given. The blood-pressure flow is a modal over
 * the page behind it rather than a screen of its own.
 */
export function PatientApp({ session, me, config, route, welcome, onSignOut, notice, clearNotice }: { session: AppSession; me: MeView; config: AuthConfig | null; route: Route; welcome: boolean; onSignOut: () => void; notice: string | null; clearNotice: () => void }) {
  const { view, error, reload } = usePatientView(session.token);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (notice) {
      setToast(notice);
      clearNotice();
    }
  }, [notice, clearNotice]);

  if (welcome) {
    return (
      <Onboarding
        view={view}
        onDone={() => {
          markWelcomed(session.personId);
          goTo(tabHash('home'));
        }}
      />
    );
  }

  const say = (s: string) => setToast(s);
  const tab: PatientTab = route.page === 'tab' ? route.tab : 'home';
  // What the bar should light up. Null on a page that is not one of these — the inbox — because
  // lighting up the wrong one is worse than lighting up none.
  const here: PatientTab | 'devices' | null = route.page === 'tab' ? route.tab : route.page === 'devices' ? 'devices' : null;
  const logging = route.page === 'bp-log';

  let body: JSX.Element;
  if (!view) {
    body = <div className="loading">{error ? <p className="form-error">{error}</p> : <p>Loading your dashboard…</p>}</div>;
  } else if (route.page === 'inbox') {
    body = <MessagesPage session={session} onChanged={reload} />;
  } else if (route.page === 'devices') {
    body = <DevicesPage session={session} onChanged={reload} />;
  } else {
    body = renderTab(tab, { session, me, config, view, reload, say, onSignOut });
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-inner">
          <a className="brand" href={tabHash('home')}>
            <HeartMark size={24} />
            <span>HeartCoach</span>
          </a>
          <nav className="topnav" aria-label="Sections">
            {NAV.map((n) => (
              <a key={n.page} href={navHref(n.page)} className={here === n.page ? 'on' : ''} aria-current={here === n.page ? 'page' : undefined}>
                <Icon name={n.icon} size={17} />
                {n.label}
              </a>
            ))}
          </nav>
          <div className="topbar-right">
            {view && view.streak.current > 0 ? (
              <span className="pill pill-soft" title={`${view.streak.current}-day streak`}>
                <span aria-hidden="true">🔥</span> {view.streak.current}
              </span>
            ) : null}
            {view ? (
              <a className="pill pill-gold" href={tabHash('rewards')} title="HeartCoins">
                <Icon name="coin" size={15} /> {fmtCoins(view.coins.balance)}
              </a>
            ) : null}
            <a className="icon-btn bell" href="#/inbox" aria-label={view?.inboxUnread ? `${view.inboxUnread} unread messages` : 'Messages'}>
              <Icon name="bell" size={19} />
              {view && view.inboxUnread > 0 ? <span className="count">{view.inboxUnread}</span> : null}
            </a>
            <UserMenu name={view?.profile.name ?? displayName(session)} label={displayName(session)} onSignOut={onSignOut} />
          </div>
        </div>
      </header>

      <main className="shell">{body}</main>

      {logging && view ? <BpLogFlow session={session} view={view} onDone={reload} say={say} onClose={() => goTo(tabHash('home'))} /> : null}
      {route.page === 'symptoms' && view ? <SymptomFlow session={session} view={view} onDone={reload} onClose={() => goTo(tabHash('home'))} /> : null}
      {route.page === 'vital-log' && view ? <VitalLogFlow session={session} view={view} kind={route.kind} onDone={reload} onClose={() => goTo(tabHash('home'))} /> : null}
      <Toast text={toast} onClose={() => setToast(null)} />
    </div>
  );
}

function renderTab(tab: PatientTab, p: { session: AppSession; me: MeView; config: AuthConfig | null; view: PatientView; reload: () => Promise<void>; say: (s: string) => void; onSignOut: () => void }): JSX.Element {
  switch (tab) {
    case 'home':
      return <HomeTab {...p} />;
    case 'health':
      return <HealthTab view={p.view} />;
    case 'coach':
      return <CoachTab session={p.session} view={p.view} reload={p.reload} say={p.say} />;
    case 'rewards':
      return <RewardsTab session={p.session} view={p.view} reload={p.reload} say={p.say} />;
    case 'profile':
      return <ProfileTab session={p.session} me={p.me} config={p.config} view={p.view} onSignOut={p.onSignOut} reload={p.reload} />;
  }
}
